import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, exhaustMap, filter, map, mergeMap, of, switchMap, tap } from 'rxjs';

import {
    AttachBlueSagePaymentGQL,
    CompleteBlueSageHoldFromQueueGQL,
    DismissBlueSagePaymentGQL,
    GetBlueSageJobLookupGQL,
    GetBlueSageUnmatchedPaymentsGQL,
    GetBlueSageUnmatchedSummaryGQL,
} from 'src/generated/graphql.generated';

import { BlueSageQueueActions } from './bluesage-queue.actions';
import { blueSageQueueFeature } from './bluesage-queue.feature';

/** Every action that should produce a fresh page. */
const RELOADS = [
    BlueSageQueueActions.pageOpened,
    BlueSageQueueActions.refreshButtonClicked,
    BlueSageQueueActions.statusFilterChanged,
    BlueSageQueueActions.pageChanged,
    BlueSageQueueActions.attachSuccess,
    BlueSageQueueActions.dismissSuccess,
] as const;

export const loadBlueSageQueue = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    getPaymentsGQL = inject(GetBlueSageUnmatchedPaymentsGQL),
) => {
    return actions$.pipe(
        ofType(...RELOADS),
        // Requirement: K8 - switchMap, never exhaustMap. This is a READ, and every
        // RELOADS trigger has already set `loading` in the reducer. exhaustMap
        // DROPS a trigger that arrives while a fetch is in flight, so no success
        // or error action ever follows it and the page sits on a spinner over
        // stale rows until something else happens to reload it. Switching to the
        // newest request is the only correct behaviour for a read.
        switchMap((action) => of(action).pipe(
            concatLatestFrom(() => [
                store.select(blueSageQueueFeature.selectStatusFilter),
                store.select(blueSageQueueFeature.selectSkip),
                store.select(blueSageQueueFeature.selectLimit),
            ]),
            // no-cache for the same reason as the settings panel: this page exists
            // to answer "what is unresolved right now", and a cached answer after
            // someone else attached a payment is worse than no answer.
            mergeMap(([ _a, status, skip, limit ]) => getPaymentsGQL.fetch(
                { status, skip, limit }, { fetchPolicy: 'no-cache' },
            ).pipe(
                map((res) => BlueSageQueueActions.loadPaymentsSuccess({
                    page: res.data.blueSageUnmatchedPayments,
                })),
            )),
            catchError((error) => of(BlueSageQueueActions.loadPaymentsError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * The badge count, reloaded on the same triggers as the page.
 *
 * A separate query rather than a field on the list, matching
 * `attentionItemsSummary`: whoever puts this number in the nav must not have to
 * fetch a page of payments to show it.
 */
export const loadBlueSageQueueSummary = createEffect((
    actions$ = inject(Actions),
    getSummaryGQL = inject(GetBlueSageUnmatchedSummaryGQL),
) => {
    return actions$.pipe(
        ofType(...RELOADS),
        // Requirement: K8 - switchMap for the same reason as the page read above.
        // With exhaustMap the badge count silently kept a pre-resolution number
        // whenever attachSuccess or dismissSuccess landed during an in-flight
        // fetch.
        switchMap((action) => of(action).pipe(
            mergeMap(() => getSummaryGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(
                map((res) => BlueSageQueueActions.loadSummarySuccess({
                    summary: res.data.blueSageUnmatchedPaymentsSummary,
                })),
            )),
            catchError((error) => of(BlueSageQueueActions.loadSummaryError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const attachBlueSagePayment = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    attachGQL = inject(AttachBlueSagePaymentGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageQueueActions.attachButtonClicked),
        // exhaustMap, not mergeMap: attaching moves money onto a job, and a
        // double-click must not become two attempts. The server refuses the
        // second anyway (the record is only resolvable while `open`), but the
        // UI should not be relying on that to stay correct.
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => store.select(blueSageQueueFeature.selectJobMatch)),
            // The RESOLVED job id, never the typed code. `blueSageJobLookup` did
            // the matching — zone-scoped, exact, ambiguity refused — so what
            // reaches the money-moving mutation is a UUID, exactly as it was
            // before an operator was allowed to type a code (K25).
            // `action` comes from the outer closure rather than the tuple: the
            // tuple's first element widens to possibly-null under the Angular
            // compiler's stricter settings, and this one is known good.
            mergeMap(([ , match ]) => {

                // Requirement: K25 - never let an unresolved code reach the
                // money-moving mutation. Both buttons are disabled until
                // `canAttach`/`canAttachAsPayment` sees a resolved job, so this
                // is unreachable through the UI today — but an empty `jobId` is
                // not something a disabled attribute should be the only thing
                // standing in the way of. Throwing lands on the effect's own
                // `catchError`, which dispatches `attachError`: the toast fires
                // and `resolvingId` clears, exactly as any other failure.
                const jobId = match?.job?.id;
                if (!jobId) {
                    throw new Error('That job code has not resolved to a job yet — nothing was attached.');
                }

                return attachGQL.mutate({
                    paymentId: action.paymentId,
                    jobId,
                    ...(action.fileAs ? { type: action.fileAs } : {}),
                }).pipe(
                    map((res) => BlueSageQueueActions.attachSuccess({
                        payment: res.data.attachBlueSagePayment,
                    })),
                );
            }),
            catchError((error) => of(BlueSageQueueActions.attachError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * Complete the resolved job's open hold with this capture (2026-09-03).
 *
 * The case: a preauth completed on the device after the middleware stopped
 * waiting arrives here as an unattributed sale, while the hold sits pending on
 * the job. Attaching would file a second transaction; this settles the hold.
 * `exhaustMap` because it moves money.
 */
export const completeBlueSageHoldFromQueue = createEffect((
    actions$ = inject(Actions),
    completeGQL = inject(CompleteBlueSageHoldFromQueueGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageQueueActions.completeHoldButtonClicked),
        exhaustMap((action) => of(action).pipe(
            mergeMap(() => completeGQL.mutate({
                paymentId: action.paymentId,
                transactionId: action.transactionId,
            }).pipe(
                map((res) => BlueSageQueueActions.completeHoldSuccess({
                    payment: res.data.completeBlueSageHoldFromQueue,
                })),
            )),
            catchError((error) => of(BlueSageQueueActions.completeHoldError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const dismissBlueSagePayment = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    dismissGQL = inject(DismissBlueSagePaymentGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageQueueActions.dismissButtonClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => store.select(blueSageQueueFeature.selectDismissReason)),
            mergeMap(([ , reason ]) => dismissGQL.mutate({
                paymentId: action.paymentId,
                reason: reason.trim(),
            }).pipe(
                map((res) => BlueSageQueueActions.dismissSuccess({
                    payment: res.data.dismissBlueSagePayment,
                })),
            )),
            catchError((error) => of(BlueSageQueueActions.dismissError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * Resolve the typed job code to a job (K25).
 *
 * Runs on selecting a row as well as on typing, because selecting pre-fills the
 * code the crew keyed on the terminal — which is usually the right answer and
 * should not need retyping to confirm.
 *
 * `debounceTime` must stay OUTSIDE `switchMap` — `of(action)` completes
 * synchronously, which makes `debounceTime` flush immediately instead of
 * waiting, defeating the debounce entirely. But running debounce outside
 * switchMap means a new action during the 300 ms window does NOT cancel an
 * in-flight lookup — the old result could land and write a stale `jobMatch`
 * for the wrong payment (CR finding 49416431).
 *
 * The fix is a correlation key: each success action carries the code and
 * paymentId it looked up. The reducer ignores a success whose key does not
 * match the current `jobCode` + `selectedId`, so a stale result from a
 * superseded lookup is silently dropped rather than written to `jobMatch`.
 */
export const lookupBlueSageJob = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    lookupGQL = inject(GetBlueSageJobLookupGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageQueueActions.jobCodeChanged, BlueSageQueueActions.paymentSelected),
        // Enough to stop a query per keystroke without feeling laggy.
        debounceTime(300),
        concatLatestFrom(() => [
            store.select(blueSageQueueFeature.selectJobCode),
            store.select(blueSageQueueFeature.selectSelectedPayment),
        ]),
        filter(([ , code ]) => Boolean(code.trim())),
        switchMap(([ , code, payment ]) => {
            const lookupKey = { code: code.trim(), paymentId: payment?.id ?? null };
            return lookupGQL.fetch(
                // The amount is what turns "does this job exist" into "can this job
                // take THIS money as a payment" — the question the two buttons ask.
                { code: code.trim(), amount: payment?.baseAmount ?? null },
                { fetchPolicy: 'no-cache' },
            ).pipe(
                map((res) => BlueSageQueueActions.jobLookupSuccess({
                    match: res.data.blueSageJobLookup,
                    lookupKey,
                })),
                catchError((error) => of(BlueSageQueueActions.jobLookupError({ error }))),
            );
        }),
    );
}, { functional: true, dispatch: true });


/**
 * Open the job an attached payment landed on.
 *
 * `dispatch: false` — navigation is the effect, and nothing in the store
 * changes. The job page loads its own data from the route.
 */
export const openAttachedBlueSageJob = createEffect((
    actions$ = inject(Actions),
    router = inject(Router),
) => {
    return actions$.pipe(
        ofType(BlueSageQueueActions.attachedJobClicked),
        filter(({ jobId }) => Boolean(jobId)),
        tap(({ jobId }) => router.navigate([ '/jobs', jobId ])),
    );
}, { functional: true, dispatch: false });
